import axios from 'axios';
import { getAccessToken, getRefreshToken } from '../utils/auth';

// API URL based on APP_ENV from .env file
const getBaseUrl = () => {
  return process.env.REACT_APP_APP_ENV === 'production'
    ? process.env.REACT_APP_PROD_API_URL
    : process.env.REACT_APP_DEV_API_URL;
};

// Create axios instance with explicit base URL
const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Activity tracking API
export const activityApi = {
  updateLastViewed: () => api.post('/api/activity/update-last-viewed'),
  getDistributorActivity: () => api.get('/api/activity/distributor-activity'),
};

// Create a separate instance for refresh token requests to avoid interceptor loop
const refreshApi = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if there's a refresh token request in progress
let isRefreshing = false;
let refreshSubscribers = [];

// Function to push callbacks to subscribers queue
const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);

// Function to execute all subscribers with the new token
const onRefreshed = (token) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

// Add access token to requests
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is not 401 or it's already been retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
    
    // Mark the request as retried
    originalRequest._retry = true;
    
    // If we're already refreshing, add this request to queue
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(api(originalRequest));
        });
      });
    }
    
    // Start refreshing process
    isRefreshing = true;
    
    try {
      // Use a separate axios instance to avoid interceptor loop
      const refreshToken = getRefreshToken();
      const response = await refreshApi.post('/api/auth/refresh', {}, {
        headers: {
          Authorization: `Bearer ${refreshToken}`
        }
      });
      
      const { access_token } = response.data;
      
      // Update token in localStorage (your auth system should handle this)
      // This will be called by your auth context when it receives the new token
      
      // Update original request auth header
      originalRequest.headers.Authorization = `Bearer ${access_token}`;
      
      // Notify all subscribers about the new token
      onRefreshed(access_token);
      
      isRefreshing = false;
      
      // Retry original request with new token
      return api(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      
      // Your auth system should handle logout on refresh failure
      // This will be triggered by your auth context
      
      return Promise.reject(refreshError);
    }
  }
);

export default api;